import { motion } from "framer-motion";
import { Link } from "wouter";
import { BookOpen, Check, Users } from "lucide-react";
import { useClubs } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Loader } from "@/components/Loader";
import { ComingSoonBadge, MeetupPlannedChip } from "./clubComponents";

export default function ClubsIndex() {
  const { t } = useT();
  const { data: clubs = [], isLoading } = useClubs();

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      <div className="max-w-3xl mx-auto">
        <header className="mb-6">
          <h1 className="text-3xl font-bold">{t("clubs.title")}</h1>
          <p className="text-dark-muted mt-1">{t("clubs.subtitle")}</p>
        </header>

        {isLoading ? (
          <div className="flex justify-center pt-12">
            <Loader />
          </div>
        ) : clubs.length === 0 ? (
          <div className="card text-center py-12">
            <BookOpen className="w-12 h-12 text-dark-muted mx-auto mb-3" />
            <p className="text-dark-muted">{t("clubs.empty")}</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {clubs.map((club, i) => (
              <motion.div key={club.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Link href={`/clubs/${club.slug}`} className="card block h-full p-0 overflow-hidden hover:ring-2 hover:ring-primary-500/40 transition">
                  {club.imageUrl ? (
                    <img src={club.imageUrl} alt="" className="w-full aspect-square object-contain bg-neutral-100 dark:bg-neutral-800" />
                  ) : (
                    <div className="w-full aspect-square bg-primary-500/15 flex items-center justify-center">
                      <BookOpen className="w-10 h-10 text-primary-500" />
                    </div>
                  )}
                  <div className="p-4 space-y-3">
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <h2 className="font-bold text-lg leading-tight">{club.name}</h2>
                        {club.isMember && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 flex-shrink-0">
                            <Check className="w-3.5 h-3.5" /> {t(club.status === "unopened" ? "clubs.onTheList" : "clubs.joined")}
                          </span>
                        )}
                      </div>
                      {club.tagline && <p className="text-sm text-dark-muted mt-1">{club.tagline}</p>}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-dark-muted">
                      <Users className="w-3.5 h-3.5" />{" "}
                      {t(club.status === "unopened" ? "clubs.interested" : "clubs.members", { count: club.memberCount })}
                    </div>
                    {club.status === "unopened" && <ComingSoonBadge />}
                    <MeetupPlannedChip city={club.city} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
